import { trackUmami } from './umami';
const SCRIPT_ID = 'google-analytics-gtag';
const PRODUCTION_HOSTNAMES = new Set(['harrydabbq.se', 'www.harrydabbq.se']);
const FUNNEL_EVENTS = new Set([
  'view_catering',
  'begin_catering_quote',
  'catering_event_details_completed',
  'select_catering_package',
  'view_catering_estimate',
  'begin_catering_contact',
  'submit_catering_quote',
  'generate_lead',
  'catering_form_error',
]);
// Only closed enums are accepted. Dynamic form/backend values are never analytics parameters.
const SAFE_EVENT_PARAMETERS = new Set(['step_id', 'error_type']);
const SAFE_STEP_IDS = new Set([
  'catering_page', 'form', 'event_date', 'package', 'estimate', 'contact', 'submit', 'success', 'error',
]);
const SAFE_ERROR_TYPES = new Set(['submission']);
const CATERING_MILESTONES = Object.freeze({
  page_view: 'view_catering',
  form_start: 'begin_catering_quote',
  event_details: 'catering_event_details_completed',
  package_selected: 'select_catering_package',
  estimate_viewed: 'view_catering_estimate',
  contact_started: 'begin_catering_contact',
  valid_submit: 'submit_catering_quote',
  lead_success: 'generate_lead',
  submission_error: 'catering_form_error',
});
const CATERING_PREREQUISITES = Object.freeze({
  form_start: 'page_view',
  event_details: 'form_start',
  package_selected: 'event_details',
  estimate_viewed: 'package_selected',
  contact_started: 'estimate_viewed',
  valid_submit: 'contact_started',
  lead_success: 'valid_submit',
  submission_error: 'valid_submit',
});

let initialized = false;
let activeMeasurementId = '';
let activeHostname = '';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export const isProductionHost = (hostname) => PRODUCTION_HOSTNAMES.has(hostname);

const analyticsOptions = (options = {}) => ({
  measurementId: options.measurementId ?? process.env.REACT_APP_GA_MEASUREMENT_ID ?? '',
  hostname: options.hostname ?? (isBrowser() ? window.location.hostname : ''),
});

export const isGoogleAnalyticsEnabled = (options = {}) => {
  const { measurementId, hostname } = analyticsOptions(options);
  return Boolean(measurementId) && isBrowser() && isProductionHost(hostname);
};

export const initGoogleAnalytics = (options = {}) => {
  const { measurementId, hostname } = analyticsOptions(options);
  if (!isGoogleAnalyticsEnabled({ measurementId, hostname })) return false;

  activeMeasurementId = measurementId;
  activeHostname = hostname;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }

  if (!initialized) {
    window.gtag('js', new Date());
    window.gtag('config', measurementId, { send_page_view: false });
    initialized = true;
  }
  return true;
};

const ensureAnalytics = () => {
  if (activeMeasurementId && isProductionHost(activeHostname) && window.gtag) return true;
  return initGoogleAnalytics();
};

// Engångsnycklar i adressen (t.ex. lösenordsåterställning) skickas aldrig till analys.
export const redactSensitiveParams = (url) => String(url).replace(/([?&](?:token|reset_token)=)[^&#]*/gi, '$1redacted');

export const trackPageView = (path) => {
  if (!isBrowser() || !ensureAnalytics()) return false;
  window.gtag('event', 'page_view', {
    page_path: redactSensitiveParams(path),
    page_location: redactSensitiveParams(window.location.href),
    page_title: document.title,
  });
  return true;
};

export const trackEvent = (eventName, parameters = {}) => {
  if (!FUNNEL_EVENTS.has(eventName) || !isBrowser()) return false;
  // Umami-funnlarna bygger på samma eventnamn. Skickas oavsett om GA är
  // aktiverat – Umami-skriptet filtrerar själv bort icke-produktionsdomäner.
  const sentToUmami = trackUmami(eventName);
  if (!ensureAnalytics()) return sentToUmami;
  const safeParameters = safeFunnelParameters(parameters);
  window.gtag('event', eventName, safeParameters);
  return true;
};

const safeFunnelParameters = (parameters = {}) => Object.fromEntries(
  Object.entries(parameters).filter(([key, value]) => (
    SAFE_EVENT_PARAMETERS.has(key)
    && typeof value === 'string'
    && (
      (key === 'step_id' && SAFE_STEP_IDS.has(value))
      || (key === 'error_type' && SAFE_ERROR_TYPES.has(value))
    )
  )),
);

/** One tracker belongs to one mounted catering page (one page visit). */
export const createCateringFunnelTracker = (dispatch = trackEvent) => {
  const seen = new Set();

  return {
    track(milestone, parameters = {}) {
      const eventName = CATERING_MILESTONES[milestone];
      const prerequisite = CATERING_PREREQUISITES[milestone];
      if (!eventName || seen.has(milestone) || (prerequisite && !seen.has(prerequisite))) return false;
      const dispatched = dispatch(eventName, safeFunnelParameters(parameters));
      if (dispatched === false) return false;
      seen.add(milestone);
      return true;
    },
  };
};
